import {HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {EMPTY, of} from 'rxjs';
import {catchError, exhaustMap, map, tap, withLatestFrom} from 'rxjs/operators';
import {API} from 'src/app/shared/constants/api.constants';
import {ErrorHandlingHttpClient} from 'src/app/shared/services/error-handling-http-client.service';

import {PaymentType} from '../../requests/constants/request-details.constants';
import {PopItemTypeModel} from '../../requests/models/pop-design-request.model';
import {Warehouse} from '../../requests/models/warehouse.model';
import {CodeNameModel} from '../../shared/models/code-name.model';
import {UtilsService} from '../../shared/services/utils.service';
import {
  lazyLoadPaymentTypes,
  loadPaymentTypes,
  loadPaymentTypesFailure,
  loadPaymentTypesSuccess,
  loadFSPaymentTypes,
  loadFSPaymentTypesSuccess,
  loadFSPaymentTypesFailure,
  lazyLoadFSPaymentTypes,
  loadPopChannelTypes,
  loadPopChannelTypesSuccess,
  loadPopChannelTypesFailure,
  lazyLoadPopChannelTypes,
  loadPopProdUsageDurationTypes,
  loadPopProdUsageDurationTypesSuccess,
  loadPopProdUsageDurationTypesFailure,
  lazyLoadPopProdUsageDurationTypes,
  loadDielines,
  loadDielinesSuccess,
  loadDielinesFailure,
  lazyLoadDielines,
  loadDataPinFileTypes,
  loadDataPinFileTypesSuccess,
  loadDataPinFileTypesFailure,
  lazyLoadDataPinFileTypes,
  loadSubstrates,
  loadSubstratesSuccess,
  loadSubstratesFailure,
  lazyLoadSubstrates,
  loadRedemptionTypes,
  loadRedemptionTypesSuccess,
  loadRedemptionTypesFailure,
  lazyLoadRedemptionTypes,
  loadRequestItemTypes,
  loadRequestItemTypesSuccess,
  loadRequestItemTypesFailure,
  lazyLoadRequestItemTypes,
  loadRegions,
  loadRegionsSuccess,
  loadRegionsFailure,
  lazyLoadRegions,
  loadWarehouses,
  loadWarehousesSuccess,
  loadWarehousesFailure,
  lazyLoadWarehouses,
  loadCompanies,
  loadCompaniesSuccess,
  loadCompaniesFailure,
  lazyLoadCompanies,
  loadPartnerGroups,
  loadPartnerGroupsSuccess,
  loadPartnerGroupsFailure,
  lazyLoadPartnerGroups,
  loadDesignLineOfBusiness,
  loadDesignLineOfBusinessSuccess,
  loadDesignLineOfBusinessFailure,
  lazyLoadDesignLineOfBusiness,
  loadArtLocationsTypes,
  loadArtLocationsTypesSuccess,
  loadArtLocationsTypesFailure,
  lazyLoadArtLocationsTypes,
  loadDesignSourceTypes,
  loadDesignSourceTypesSuccess,
  loadDesignSourceTypesFailure,
  lazyLoadDesignSourceTypes,
  loadChannelTypes,
  loadChannelTypesSuccess,
  loadChannelTypesFailure,
  lazyLoadChannelTypes,
  loadInventoryTypes,
  loadInventoryTypesSuccess,
  loadInventoryTypesFailure,
  lazyLoadInventoryTypes,
  loadComponentTypes,
  loadComponentTypesSuccess,
  loadComponentTypesFailure,
  lazyLoadComponentTypes,
  loadPopItemTypes,
  loadPopItemTypesSuccess,
  loadPopItemTypesFailure,
  lazyLoadPopItemTypes,
  loadColorTypes,
  loadColorTypesSuccess,
  loadColorTypesFailure,
  lazyLoadColorTypes,
  loadDimensionTypes,
  loadDimensionTypesSuccess,
  loadDimensionTypesFailure,
  lazyLoadDimensionTypes,
  loadFinancialTypes,
  loadFinancialTypesSuccess,
  loadFinancialTypesFailure,
  lazyLoadFinancialTypes,
  loadPopProdEventTypes,
  loadPopProdEventTypesSuccess,
  loadPopProdEventTypesFailure,
  lazyLoadPopProdEventTypes,
  loadPopProdFundingTypes,
  loadPopProdFundingTypesSuccess,
  loadPopProdFundingTypesFailure,
  lazyLoadPopProdFundingTypes,
  loadPopProdItemTypes,
  loadPopProdItemTypesSuccess,
  loadPopProdItemTypesFailure,
  lazyLoadPopProdItemTypes,
  loadPopProdCuttingTypes,
  loadPopProdCuttingTypesSuccess,
  loadPopProdCuttingTypesFailure,
  lazyLoadPopProdCuttingTypes,
  loadPopProdMaterialTypes,
  loadPopProdMaterialTypesSuccess,
  loadPopProdMaterialTypesFailure,
  lazyLoadPopProdMaterialTypes,
  loadPopProdTapeTypes,
  loadPopProdTapeTypesSuccess,
  loadPopProdTapeTypesFailure,
  lazyLoadPopProdTapeTypes,
  loadPopProdInventoryTypes,
  loadPopProdInventoryTypesSuccess,
  loadPopProdInventoryTypesFailure,
  lazyLoadPopProdInventoryTypes,
  loadPrinters,
  loadPrintersSuccess,
  loadPrintersFailure,
  lazyLoadPrinters,
  loadBifComponentTypes,
  loadBifComponentTypesSuccess,
  loadBifComponentTypesFailure,
  lazyLoadBifComponentTypes,
  loadBifFulfillmentTypes,
  loadBifFulfillmentTypesSuccess,
  loadBifFulfillmentTypesFailure,
  lazyLoadBifFulfillmentTypes,
  loadThirdPartyOrderTypes,
  loadThirdPartyOrderTypesSuccess,
  loadThirdPartyOrderTypesFailure,
  lazyLoadThirdPartyOrderTypes,
  loadThirdPartyDataPinFileTypes,
  loadThirdPartyDataPinFileTypesSuccess,
  loadThirdPartyDataPinFileTypesFailure,
  lazyLoadThirdPartyDataPinFileTypes,
  loadThirdPartyPrintOwnerTypes,
  loadThirdPartyPrintOwnerTypesSuccess,
  loadThirdPartyPrintOwnerTypesFailure,
  lazyLoadThirdPartyPrintOwnerTypes,
  loadIhComponents,
  loadIhComponentsSuccess,
  loadIhComponentsFailure,
  lazyLoadIhComponents,
  loadIhHealthPlans,
  loadIhHealthPlansSuccess,
  loadIhHealthPlansFailure,
  lazyLoadIhHealthPlans,
  loadIhHealthPrograms,
  loadIhHealthProgramsSuccess,
  loadIhHealthProgramsFailure,
  lazyLoadIhHealthPrograms,
  loadIhPriorities,
  loadIhPrioritiesSuccess,
  loadIhPrioritiesFailure,
  lazyLoadIhPriorities,
  loadReturnFileTypes,
  loadReturnFileTypesSuccess,
  loadReturnFileTypesFailure,
  lazyLoadReturnFileTypes,
  loadImageFormatTypes,
  loadImageFormatTypesSuccess,
  loadImageFormatTypesFailure,
  lazyLoadImageFormatTypes
} from './refdata.actions';
import {refDataInitialState as refdataInitialState} from './refdata.reducer';
import {
  selectArtLocationTypes,
  selectBifComponentTypes,
  selectBifFulfillmentTypes,
  selectChannelTypes,
  selectColorTypes,
  selectCompanies,
  selectComponentTypes,
  selectDataPinFileTypes,
  selectDesignLineOfBusiness,
  selectDesignSourceTypes,
  selectDielines,
  selectDimensionTypes,
  selectFinancialTypes,
  selectFSPaymentTypes,
  selectIhComponents,
  selectIhHealthPlans,
  selectIhHealthPrograms,
  selectIhPriorities,
  selectImageFormatTypes,
  selectInventoryTypes,
  selectPartnerGroups,
  selectPaymentTypes,
  selectPopChannelTypes,
  selectPopItemTypes,
  selectPopProdCuttingTypes,
  selectPopProdEventTypes,
  selectPopProdFundingTypes,
  selectPopProdInventoryTypes,
  selectPopProdItemTypes,
  selectPopProdMaterialTypes,
  selectPopProdTapeTypes,
  selectPopProdUsageDurationTypes,
  selectPrinters,
  selectRedemptionTypes,
  selectRegions,
  selectRequestItemTypes,
  selectReturnFileTypes,
  selectSubstrates,
  selectThirdPartyDataPinFileTypes,
  selectThirdPartyOrderTypes,
  selectThirdPartyPrintOwnerTypes,
  selectWarehouses
} from './refdata.selectors';

@Injectable()
export class RequestRefDataEffects {
  constructor(
    private errorHandlingHttpClient: ErrorHandlingHttpClient,
    private actions$: Actions,
    private store: Store,
    private utilsService: UtilsService
  ) {}

  /**
   * PaymentTypes
   */
  loadPaymentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPaymentTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<PaymentType[]>(`${API.paymentTypes}`).pipe(
          map(data => loadPaymentTypesSuccess({paymentTypes: data})),
          catchError(() => of(loadPaymentTypesFailure()))
        )
      )
    )
  );

  lazyLoadPaymentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPaymentTypes),
      withLatestFrom(this.store.select(selectPaymentTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.paymentTypes === storedData) {
          return of(loadPaymentTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * Financial Service PaymentTypes
   */
  loadFSPaymentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFSPaymentTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<PaymentType[]>(`${API.fsPaymentTypes}`).pipe(
          map(data => loadFSPaymentTypesSuccess({fsPaymentTypes: data})),
          catchError(() => of(loadFSPaymentTypesFailure()))
        )
      )
    )
  );

  lazyLoadFSPaymentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadFSPaymentTypes),
      withLatestFrom(this.store.select(selectFSPaymentTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.fsPaymentTypes === storedData) {
          return of(loadFSPaymentTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * Dielines
   */
  loadDielines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDielines),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.dielines}`).pipe(
          map(data => loadDielinesSuccess({dielines: data})),
          catchError(() => of(loadDielinesFailure()))
        )
      )
    )
  );

  lazyLoadDielines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadDielines),
      withLatestFrom(this.store.select(selectDielines)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.dielines === storedData) {
          return of(loadDielines());
        }
        return EMPTY;
      })
    )
  );

  /**
   * DataPinFileTypes
   */
  loadDataPinFileTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDataPinFileTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.dataPINfileTypes}`).pipe(
          map(data => loadDataPinFileTypesSuccess({dataPinFileTypes: data})),
          catchError(() => of(loadDataPinFileTypesFailure()))
        )
      )
    )
  );
  lazyLoadDataPinFileTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadDataPinFileTypes),
      withLatestFrom(this.store.select(selectDataPinFileTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.dataPinFileTypes === storedData) {
          return of(loadDataPinFileTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * Substrates
   */
  loadSubstrates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSubstrates),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.substrates}`).pipe(
          map(data => loadSubstratesSuccess({substrates: data})),
          catchError(() => of(loadSubstratesFailure()))
        )
      )
    )
  );
  lazyLoadSubstrates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadSubstrates),
      withLatestFrom(this.store.select(selectSubstrates)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.substrates === storedData) {
          return of(loadSubstrates());
        }
        return EMPTY;
      })
    )
  );

  /**
   * RedemptionTypes
   */
  loadRedemptionTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRedemptionTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.redemptionTypes}`).pipe(
          map(data => loadRedemptionTypesSuccess({redemptionTypes: data})),
          catchError(() => of(loadRedemptionTypesFailure()))
        )
      )
    )
  );
  lazyLoadRedemptionTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadRedemptionTypes),
      withLatestFrom(this.store.select(selectRedemptionTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.redemptionTypes === storedData) {
          return of(loadRedemptionTypes());
        }
        return EMPTY;
      })
    )
  );
  /**
   * RequestItemTypes
   */
  loadRequestItemTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRequestItemTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.requestItemsTypes}`).pipe(
          map(data => loadRequestItemTypesSuccess({requestItemTypes: data})),
          catchError(() => of(loadRequestItemTypesFailure()))
        )
      )
    )
  );
  lazyLoadRequestItemTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadRequestItemTypes),
      withLatestFrom(this.store.select(selectRequestItemTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.requestItemTypes === storedData) {
          return of(loadRequestItemTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * Regions
   */
  loadRegions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRegions),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.regions}`).pipe(
          map(data => loadRegionsSuccess({regions: data})),
          catchError(() => of(loadRegionsFailure()))
        )
      )
    )
  );
  lazyLoadRegions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadRegions),
      withLatestFrom(this.store.select(selectRegions)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.regions === storedData) {
          return of(loadRegions());
        }
        return EMPTY;
      })
    )
  );

  /**
   * Warehouses
   */
  loadWarehouses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadWarehouses),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<Warehouse[]>(`${API.warehouses}`).pipe(
          map(data =>
            loadWarehousesSuccess({
              warehouses: [...data].sort(this.utilsService.sortAZNames)
            })
          ),
          catchError(() => of(loadWarehousesFailure()))
        )
      )
    )
  );
  lazyLoadWarehouses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadWarehouses),
      withLatestFrom(this.store.select(selectWarehouses)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.warehouses === storedData) {
          return of(loadWarehouses());
        }
        return EMPTY;
      })
    )
  );

  /**
   * Companies
   */
  loadCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCompanies),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.companies}`).pipe(
          map(data => loadCompaniesSuccess({companies: data})),
          catchError(() => of(loadCompaniesFailure()))
        )
      )
    )
  );
  lazyLoadCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadCompanies),
      withLatestFrom(this.store.select(selectCompanies)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.companies === storedData) {
          return of(loadCompanies());
        }
        return EMPTY;
      })
    )
  );

  /**
   * PartnerGroups
   */
  loadPartnerGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPartnerGroups),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.lineOfBusinessTypes}`).pipe(
          map(data => loadPartnerGroupsSuccess({partnerGroups: data})),
          catchError(() => of(loadPartnerGroupsFailure()))
        )
      )
    )
  );
  lazyLoadPartnerGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPartnerGroups),
      withLatestFrom(this.store.select(selectPartnerGroups)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.partnerGroups === storedData) {
          return of(loadPartnerGroups());
        }
        return EMPTY;
      })
    )
  );

  /**
   * DesignLineOfBusiness
   */
  loadDesignLineOfBusiness$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDesignLineOfBusiness),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.designLineOfBusinessTypes}`).pipe(
          map(data => loadDesignLineOfBusinessSuccess({designLineOfBusiness: data})),
          catchError(() => of(loadDesignLineOfBusinessFailure()))
        )
      )
    )
  );
  lazyLoadDesignLineOfBusiness$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadDesignLineOfBusiness),
      withLatestFrom(this.store.select(selectDesignLineOfBusiness)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.designLineOfBusiness === storedData) {
          return of(loadDesignLineOfBusiness());
        }
        return EMPTY;
      })
    )
  );

  /**
   * ArtLocationTypes
   */
  loadArtLocationTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadArtLocationsTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.artLocationTypes}`).pipe(
          map(data => loadArtLocationsTypesSuccess({artLocationTypes: data})),
          catchError(() => of(loadArtLocationsTypesFailure()))
        )
      )
    )
  );
  lazyLoadArtLocationTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadArtLocationsTypes),
      withLatestFrom(this.store.select(selectArtLocationTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.artLocationTypes === storedData) {
          return of(loadArtLocationsTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * DesignSourceTypes
   */
  loadDesignSourceTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDesignSourceTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.designSourceTypes}`).pipe(
          map(data => loadDesignSourceTypesSuccess({designSourceTypes: data})),
          catchError(() => of(loadDesignSourceTypesFailure()))
        )
      )
    )
  );
  lazyLoadDesignSourceTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadDesignSourceTypes),
      withLatestFrom(this.store.select(selectDesignSourceTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.designSourceTypes === storedData) {
          return of(loadDesignSourceTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * ChannelTypes
   */
  loadChannelTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadChannelTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.chnTypes}`).pipe(
          map(data => loadChannelTypesSuccess({channelTypes: data})),
          catchError(() => of(loadChannelTypesFailure()))
        )
      )
    )
  );
  lazyLoadChannelTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadChannelTypes),
      withLatestFrom(this.store.select(selectChannelTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.channelTypes === storedData) {
          return of(loadChannelTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * PopChannelTypes
   */
  loadPopChannelTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPopChannelTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.popChannelTypes}`).pipe(
          map(data => loadPopChannelTypesSuccess({popChannelTypes: data})),
          catchError(() => of(loadPopChannelTypesFailure()))
        )
      )
    )
  );
  lazyLoadPopChannelTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPopChannelTypes),
      withLatestFrom(this.store.select(selectPopChannelTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.popChannelTypes === storedData) {
          return of(loadPopChannelTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * InventoryTypes
   */
  loadInventoryTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadInventoryTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.inventoryTypes}`).pipe(
          map(data => loadInventoryTypesSuccess({inventoryTypes: data})),
          catchError(() => of(loadInventoryTypesFailure()))
        )
      )
    )
  );
  lazyLoadInventoryTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadInventoryTypes),
      withLatestFrom(this.store.select(selectInventoryTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.inventoryTypes === storedData) {
          return of(loadInventoryTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * ComponentTypes
   */
  loadComponentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadComponentTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.fsComponentTypes}`).pipe(
          map(data => loadComponentTypesSuccess({componentTypes: data})),
          catchError(() => of(loadComponentTypesFailure()))
        )
      )
    )
  );
  lazyLoadComponentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadComponentTypes),
      withLatestFrom(this.store.select(selectComponentTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.componentTypes === storedData) {
          return of(loadComponentTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * PopItemTypes
   */
  loadPopItemTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPopItemTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<PopItemTypeModel[]>(API.popItemTypes).pipe(
          map(data => loadPopItemTypesSuccess({popItemTypes: data})),
          catchError(() => of(loadPopItemTypesFailure()))
        )
      )
    )
  );
  lazyLoadPopItemTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPopItemTypes),
      withLatestFrom(this.store.select(selectPopItemTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.popItemTypes === storedData) {
          return of(loadPopItemTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * ColorTypes
   */
  loadColorTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadColorTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.colorTypes).pipe(
          map(data => loadColorTypesSuccess({colorTypes: data})),
          catchError(() => of(loadColorTypesFailure()))
        )
      )
    )
  );
  lazyLoadColorTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadColorTypes),
      withLatestFrom(this.store.select(selectColorTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.colorTypes === storedData) {
          return of(loadColorTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * DimensionTypes
   */
  loadDimensionTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDimensionTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.dimensionTypes).pipe(
          map(data => loadDimensionTypesSuccess({dimensionTypes: data})),
          catchError(() => of(loadDimensionTypesFailure()))
        )
      )
    )
  );
  lazyLoadDimensionTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadDimensionTypes),
      withLatestFrom(this.store.select(selectDimensionTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.dimensionTypes === storedData) {
          return of(loadDimensionTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * FinancialTypes
   */
  loadFinancialTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFinancialTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.financialTypes).pipe(
          map(data => loadFinancialTypesSuccess({financialTypes: data})),
          catchError(() => of(loadFinancialTypesFailure()))
        )
      )
    )
  );
  lazyLoadFinancialTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadFinancialTypes),
      withLatestFrom(this.store.select(selectFinancialTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.financialTypes === storedData) {
          return of(loadFinancialTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * PopProdEventTypes
   */
  loadPopProdEventTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPopProdEventTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.ppEventTypes).pipe(
          map(data => loadPopProdEventTypesSuccess({popProdEventTypes: data})),
          catchError(() => of(loadPopProdEventTypesFailure()))
        )
      )
    )
  );

  lazyLoadPopProdEventTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPopProdEventTypes),
      withLatestFrom(this.store.select(selectPopProdEventTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.popProdEventTypes === storedData) {
          return of(loadPopProdEventTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * PopProdFundingTypes
   */
  loadPopProdFundingTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPopProdFundingTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.ppFundingTypes).pipe(
          map(data => loadPopProdFundingTypesSuccess({popProdFundingTypes: data})),
          catchError(() => of(loadPopProdFundingTypesFailure()))
        )
      )
    )
  );

  lazyLoadPopProdFundingTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPopProdFundingTypes),
      withLatestFrom(this.store.select(selectPopProdFundingTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.popProdFundingTypes === storedData) {
          return of(loadPopProdFundingTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * PopProdUsageDurationTypes
   */
  loadPopProdUsageDurationTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPopProdFundingTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.ppUsageDurationTypes).pipe(
          map(data => loadPopProdUsageDurationTypesSuccess({popUsageDurationTypes: data})),
          catchError(() => of(loadPopProdUsageDurationTypesFailure()))
        )
      )
    )
  );

  lazyLoadPopProdUsageDurationTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPopProdUsageDurationTypes),
      withLatestFrom(this.store.select(selectPopProdUsageDurationTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.popUsageDurationTypes === storedData) {
          return of(loadPopProdUsageDurationTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * PopProdItemTypes
   */
  loadPopProdItemTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPopProdItemTypes),
      exhaustMap(() => {
        const params = new HttpParams().append('productionOnlyIncluded', 'true');
        return this.errorHandlingHttpClient.get<PopItemTypeModel[]>(API.popItemTypes, {params}).pipe(
          map(data => loadPopProdItemTypesSuccess({popProdItemTypes: data})),
          catchError(() => of(loadPopProdItemTypesFailure()))
        );
      })
    )
  );

  lazyLoadPopProdItemTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPopProdItemTypes),
      withLatestFrom(this.store.select(selectPopProdItemTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.popProdItemTypes === storedData) {
          return of(loadPopProdItemTypes());
        }
        return EMPTY;
      })
    )
  );
  /**
   * PopProdCuttingTypes
   */
  loadPopProdCuttingTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPopProdCuttingTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.ppCuttingTypes).pipe(
          map(popProdCuttingTypes => loadPopProdCuttingTypesSuccess({popProdCuttingTypes})),
          catchError(() => of(loadPopProdCuttingTypesFailure()))
        )
      )
    )
  );

  lazyLoadPopProdCuttingTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPopProdCuttingTypes),
      withLatestFrom(this.store.pipe(select(selectPopProdCuttingTypes))),
      exhaustMap(([, storedData]) => (refdataInitialState.popProdCuttingTypes === storedData ? of(loadPopProdCuttingTypes()) : EMPTY))
    )
  );
  /**
   * PopProdMaterialTypes
   */
  loadPopProdMaterialTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPopProdMaterialTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.ppMaterialTypes).pipe(
          map(popProdMaterialTypes => loadPopProdMaterialTypesSuccess({popProdMaterialTypes})),
          catchError(() => of(loadPopProdMaterialTypesFailure()))
        )
      )
    )
  );

  lazyLoadPopProdMaterialTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPopProdMaterialTypes),
      withLatestFrom(this.store.pipe(select(selectPopProdMaterialTypes))),
      exhaustMap(([, storedData]) => (refdataInitialState.popProdMaterialTypes === storedData ? of(loadPopProdMaterialTypes()) : EMPTY))
    )
  );
  /**
   * PopProdTapeTypes
   */
  loadPopProdTapeTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPopProdTapeTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.ppTapeTypes).pipe(
          map(popProdTapeTypes => loadPopProdTapeTypesSuccess({popProdTapeTypes})),
          catchError(() => of(loadPopProdTapeTypesFailure()))
        )
      )
    )
  );

  lazyLoadPopProdTapeTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPopProdTapeTypes),
      withLatestFrom(this.store.pipe(select(selectPopProdTapeTypes))),
      exhaustMap(([, storedData]) => (refdataInitialState.popProdTapeTypes === storedData ? of(loadPopProdTapeTypes()) : EMPTY))
    )
  );
  /**
   * PopProdInventoryTypes
   */
  loadPopProdInventoryTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPopProdInventoryTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.ppInventoryTypes).pipe(
          map(data => loadPopProdInventoryTypesSuccess({popProdInventoryTypes: data})),
          catchError(() => of(loadPopProdInventoryTypesFailure()))
        )
      )
    )
  );
  lazyLoadPopProdInventoryTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPopProdInventoryTypes),
      withLatestFrom(this.store.select(selectPopProdInventoryTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.popProdInventoryTypes === storedData) {
          return of(loadPopProdInventoryTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * Printers
   */
  loadPrinters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPrinters),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<any[]>(API.printers).pipe(
          tap(data => this.utilsService.consoleListPrinters(data)),
          map(data =>
            loadPrintersSuccess({
              printers: [...data].sort(this.utilsService.sortAZNames)
            })
          ),
          catchError(() => of(loadPrintersFailure()))
        )
      )
    )
  );
  lazyLoadPrinters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadPrinters),
      withLatestFrom(this.store.select(selectPrinters)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.printers === storedData) {
          return of(loadPrinters());
        }
        return EMPTY;
      })
    )
  );

  /**
   * BifComponentTypes
   */
  loadBifComponentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBifComponentTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.bifComponentTypes}`).pipe(
          map(data => loadBifComponentTypesSuccess({bifComponentTypes: data})),
          catchError(() => of(loadBifComponentTypesFailure()))
        )
      )
    )
  );
  lazyLoadBifComponentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadBifComponentTypes),
      withLatestFrom(this.store.select(selectBifComponentTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.bifComponentTypes === storedData) {
          return of(loadBifComponentTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * BifFulfillmentTypes
   */
  loadBifFulfillmentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBifFulfillmentTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.bifFulfillmentTypes}`).pipe(
          map(data => loadBifFulfillmentTypesSuccess({bifFulfillmentTypes: data})),
          catchError(() => of(loadBifFulfillmentTypesFailure()))
        )
      )
    )
  );
  lazyLoadBifFulfillmentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadBifFulfillmentTypes),
      withLatestFrom(this.store.select(selectBifFulfillmentTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.bifFulfillmentTypes === storedData) {
          return of(loadBifFulfillmentTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * ThirdPartyOrderTypes
   */
  loadThirdPartyOrderTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadThirdPartyOrderTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.thirdPartyOrderTypes}`).pipe(
          map(data => loadThirdPartyOrderTypesSuccess({thirdPartyOrderTypes: data})),
          catchError(() => of(loadThirdPartyOrderTypesFailure()))
        )
      )
    )
  );
  lazyThirdPartyOrderTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadThirdPartyOrderTypes),
      withLatestFrom(this.store.select(selectThirdPartyOrderTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.thirdPartyOrderTypes === storedData) {
          return of(loadThirdPartyOrderTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * ThirdPartyDataPinFileTypes
   */
  loadThirdPartyDataPinFileTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadThirdPartyDataPinFileTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.thirdPartyDataPinFileTypes}`).pipe(
          map(data => loadThirdPartyDataPinFileTypesSuccess({thirdPartyDataPinFileTypes: data})),
          catchError(() => of(loadThirdPartyDataPinFileTypesFailure()))
        )
      )
    )
  );
  lazyLoadThirdPartyDataPinFileTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadThirdPartyDataPinFileTypes),
      withLatestFrom(this.store.select(selectThirdPartyDataPinFileTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.thirdPartyDataPinFileTypes === storedData) {
          return of(loadThirdPartyDataPinFileTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * ThirdPartyPrintOwnerTypes
   */
  loadThirdPartyPrintOwnerTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadThirdPartyPrintOwnerTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<Array<CodeNameModel & {internal: boolean}>>(`${API.thirdPartyPrintOwnerTypes}`).pipe(
          map(data => loadThirdPartyPrintOwnerTypesSuccess({thirdPartyPrintOwnerTypes: data})),
          catchError(() => of(loadThirdPartyPrintOwnerTypesFailure()))
        )
      )
    )
  );
  lazyThirdPartyPrintOwnerTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadThirdPartyPrintOwnerTypes),
      withLatestFrom(this.store.select(selectThirdPartyPrintOwnerTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.thirdPartyPrintOwnerTypes === storedData) {
          return of(loadThirdPartyPrintOwnerTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * IhComponents
   */
  loadIhComponents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadIhComponents),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.ihComponents).pipe(
          map(data => loadIhComponentsSuccess({ihComponents: data})),
          catchError(() => of(loadIhComponentsFailure()))
        )
      )
    )
  );
  lazyLoadIhComponents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadIhComponents),
      withLatestFrom(this.store.select(selectIhComponents)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.ihComponents === storedData) {
          return of(loadIhComponents());
        }
        return EMPTY;
      })
    )
  );

  /**
   * IhHealthPlans
   */
  loadIhHealthPlans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadIhHealthPlans),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.ihHealthPlans).pipe(
          map(data => loadIhHealthPlansSuccess({ihHealthPlans: data})),
          catchError(() => of(loadIhHealthPlansFailure()))
        )
      )
    )
  );
  lazyLoadIhHealthPlans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadIhHealthPlans),
      withLatestFrom(this.store.select(selectIhHealthPlans)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.ihHealthPlans === storedData) {
          return of(loadIhHealthPlans());
        }
        return EMPTY;
      })
    )
  );

  /**
   * IhHealthPrograms
   */
  loadIhHealthPrograms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadIhHealthPrograms),
      exhaustMap(action =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(`${API.ihHealthPrograms}?healthPlanCode=${action.healthPlanCode}`).pipe(
          map(data => loadIhHealthProgramsSuccess({ihHealthPrograms: data})),
          catchError(() => of(loadIhHealthProgramsFailure()))
        )
      )
    )
  );
  lazyLoadIhHealthPrograms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadIhHealthPrograms),
      withLatestFrom(this.store.select(selectIhHealthPrograms)),
      exhaustMap(([{healthPlanCode}, storedData]) => {
        if (refdataInitialState.ihHealthPrograms === storedData) {
          return of(loadIhHealthPrograms({healthPlanCode}));
        }
        return EMPTY;
      })
    )
  );

  /**
   * IhPriorities
   */
  loadIhPriorities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadIhPriorities),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<Array<CodeNameModel & {value: number}>>(API.ihPriorities).pipe(
          map(data => loadIhPrioritiesSuccess({ihPriorities: data})),
          catchError(() => of(loadIhPrioritiesFailure()))
        )
      )
    )
  );
  lazyLoadIhPriorities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadIhPriorities),
      withLatestFrom(this.store.select(selectIhPriorities)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.ihPriorities === storedData) {
          return of(loadIhPriorities());
        }
        return EMPTY;
      })
    )
  );

  /**
   * ReturnFileTypes
   */
  loadReturnFileTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReturnFileTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.thirdPartyReturnFileTypes).pipe(
          map(data => loadReturnFileTypesSuccess({returnFileTypes: data})),
          catchError(() => of(loadReturnFileTypesFailure()))
        )
      )
    )
  );
  lazyLoadReturnFileTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadReturnFileTypes),
      withLatestFrom(this.store.select(selectReturnFileTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.returnFileTypes === storedData) {
          return of(loadReturnFileTypes());
        }
        return EMPTY;
      })
    )
  );

  /**
   * ImageFormatTypes
   */
  loadImageFormatTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadImageFormatTypes),
      exhaustMap(() =>
        this.errorHandlingHttpClient.get<CodeNameModel[]>(API.imageFormatTypes).pipe(
          map(data => loadImageFormatTypesSuccess({imageFormatTypes: data})),
          catchError(() => of(loadImageFormatTypesFailure()))
        )
      )
    )
  );
  lazyLoadImageFormatTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lazyLoadImageFormatTypes),
      withLatestFrom(this.store.select(selectImageFormatTypes)),
      exhaustMap(([, storedData]) => {
        if (refdataInitialState.imageFormatTypes === storedData) {
          return of(loadImageFormatTypes());
        }
        return EMPTY;
      })
    )
  );
}
